import {enableProdMode, ErrorHandler, inject, provideAppInitializer, provideZoneChangeDetection} from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from './environments/environment';
import { VERSION } from './app/version';
import { shouldReportToSentry, SentryHttpLike } from './app/shared/classes/sentry-filter';
import { Session } from './app/services/session.service';
import { AuthService } from './app/services/auth.service';
import { IdleWatcherService } from './app/services/idle-watcher.service';
import { EnvironmentService } from './app/services/environment.service';
import { ConnectionService } from './app/services/connection.service';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi, withJsonpSupport, withXhr } from '@angular/common/http';
import { RequestCountHttpInterceptorService } from './app/services/request-count-http-interceptor.service';
import { AuthInterceptor } from './app/shared/classes/auth-interceptor';
import { ErrorInterceptor } from './app/shared/classes/error-interceptor';
import { bearerInterceptor } from './app/shared/classes/bearer-interceptor';
import { wafTokenInterceptor } from './app/shared/classes/waf-token-interceptor';
import { staleBundleWriteGuardInterceptor } from './app/shared/classes/stale-bundle-write-guard.interceptor';
import { TokenService } from './app/services/token.service';
import { provideNgxStripe } from 'ngx-stripe';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {provideNgxPendo} from 'ngx-pendo';
import { AppComponent } from './app/components/app/app.component';
import {ForceSSLService} from "./app/services/force-ssl.service";
import {provideRouter, TitleStrategy, withRouterConfig} from "@angular/router";
import {appRoutes} from "./app/app.routes";
import {APP_ROUTER_CONFIG} from "./app/app.router-config";
import {TitleStrategyService} from "./app/services/title-strategy.service";
import {provideNativeDateAdapter} from "@angular/material/core";
import {OVERLAY_DEFAULT_CONFIG} from "@angular/cdk/overlay";
import {DatePipe} from "@angular/common";
import {provideScrollbarOptions} from "ngx-scrollbar";

if (environment.production) {
  enableProdMode();
}

if (environment.sentryDsn) {
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.sentryEnvironment,
    release: VERSION,
    // Sentry 11 dropped `sendDefaultPii` and made every category default to
    // ON (incl. IP inference via `userInfo`). Pin the old `sendDefaultPii:
    // false` posture explicitly — no IP/user, cookies, headers, bodies or
    // query strings (invite/verification tokens travel in query params).
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      stackFrameVariables: false,
      // Options the original lockdown missed; they also default ON. None of their
      // integrations (GraphQL, AI, DB, queues) run in the browser bundle; pinned
      // off so adding one later cannot silently start collecting payloads.
      graphQL: { document: false, variables: false },
      genAI: { inputs: false, outputs: false },
      // Added in Sentry 11.9, also default ON.
      mcp: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.05,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      // The rule lives in `sentry-filter.ts` so it can be tested — bootstrap
      // code is the one place nothing runs a spec over, and this decides what
      // we can SEE in production.
      //
      // The cast is needed because most `/app/*` failures arrive as STRINGS
      // (`ApiV2Service.errorHandle`) and so carry no `status` — though uploads
      // and `preserveErrorResponse` puts do arrive intact. See that file's
      // header for which is which.
      return shouldReportToSentry(hint?.originalException as SentryHttpLike | undefined)
        ? event
        : null;
    },
  });
}

bootstrapApplication(AppComponent, {
    providers: [
      provideZoneChangeDetection(),
      provideNgxPendo({
        pendoApiKey: environment.pendoApiKey,
        pendoScriptOrigin: environment.production ? environment.s3Bucket + "/assets/js/pendo" : "",
        //pendoIdFormatter: (value: any) => value.toString().toLowerCase()
      }),
      provideRouter(appRoutes, withRouterConfig(APP_ROUTER_CONFIG)),
      provideNativeDateAdapter(),
      // Every <ng-scrollbar> usage app-wide relies on this library's own defaults
      // (appearance: 'native', visibility: 'native') because no global options were
      // ever configured. ngx-scrollbar ALWAYS renders its own custom track/thumb DOM
      // (it disables the real OS scrollbar via `scrollbar-width: none` unconditionally)
      // — 'appearance' only controls whether that custom track reserves layout space
      // ('native', the library default) or floats over the content with no reserved
      // space ('compact'). We keep 'native' (space-reserving) explicit here so the
      // fixed-width custom track in _ui_components.scss has room to render without
      // clipping content. The real behavior change is 'visibility': the library
      // default ('native') only paints the thumb/track while the content is
      // scrollable; 'visible' keeps it persistently visible, which is what the
      // existing (previously dead) scrollbar-track/thumb CSS was written for.
      provideScrollbarOptions({
        appearance: 'native',
        visibility: 'visible',
      }),
      // CDK v21 renders overlays via the native Popover API (browser TOP LAYER),
      // which paints above ANY z-index — so third-party overlays that mount
      // body-level max-z-index divs (Stripe Financial Connections bank-auth,
      // etc.) appear BEHIND our dialogs. Opt out globally: classic z-index
      // overlay container (pre-v21 behavior) coexists correctly with them.
      { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
      { provide: TitleStrategy, useClass: TitleStrategyService},
      Session,
      AuthService,
      IdleWatcherService,
      EnvironmentService,
      ConnectionService,
      DatePipe,
      {
          provide: HTTP_INTERCEPTORS,
          useClass: RequestCountHttpInterceptorService,
          multi: true,
      },
      {
          provide: HTTP_INTERCEPTORS,
          useClass: AuthInterceptor,
          multi: true
      },
      {
          provide: HTTP_INTERCEPTORS,
          useClass: ErrorInterceptor,
          multi: true
      },
      provideHttpClient(withXhr(), withInterceptorsFromDi(), withInterceptors([staleBundleWriteGuardInterceptor, bearerInterceptor, wafTokenInterceptor]), withJsonpSupport()),
      provideNgxStripe(environment.stripe_publishable_key),
      provideAnimations(),
      { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }) },
      // Ensure startup side-effects formerly triggered by AppModule constructor still run
      provideAppInitializer(() => {
        // Force construction of these services at app startup for their side-effects
        inject(ConnectionService);
        inject(IdleWatcherService);
        inject(ForceSSLService);
        // @hybrid (WEA-510): kick off the bridge token fetch when gate is on
        inject(TokenService).boot();
      })
    ]
})
  .catch(err => {
    Sentry.captureException(err);
    console.error(err);
  });

